// Sprint 5.6 (D9) — Subagent (Agent / Task) collapsible sub-chat panel.
// Sprint ai-elements-rendering (D-026) — rebuilt as a RECURSIVE tree on
// the AI Elements ChainOfThought primitives.
//
// When the parent agent calls Anthropic's `Task` tool (or any future
// `Agent` variant), the spawned sub-agent emits its own assistant_text
// / tool_use / tool_result events. Anthropic stamps each one with
// `parent_tool_use_id` pointing back to the parent's tool_use id — and
// since Claude Code v2.1.172 subagents can spawn their own subagents up
// to 5 levels deep. The panel receives the full `childrenByParent` map
// from `groupBySubagent()` and recurses: a child that is itself a
// Task/Agent tool_use renders as a nested SubagentPanel at depth+1.
//
// Header convention (all pre-existing segments preserved — spec §3.2):
//   [Nk] Subagent · <subagent_type> · <model> · N tools (+M) · X.Xs
// where the depth badge (N1, N2, …) and the `(+M)` descendant-tool count
// render ONLY when the tree actually nests — a depth-1-only history
// renders identically to the flat pre-sprint panel (spec §6.7
// backward-compat gate).
//
// C11 — when a subagent errors (final tool_result has is_error) the
// header turns rose. D-msg-12 — the RAW prompt + final result <pre>
// blocks are developer-only. D-msg-16 — the tool-count segment is
// suppressed at 0.
//
// Sprint subagent-panel-ux (2026-07-22): D-sap-1 — the panel fills its
// border width at every depth (`max-w-none` overrides the primitive's
// max-w-prose cap at the call site). D-sap-2 — the running state shows
// an animated Loader in the header (glanceable while collapsed), not a
// static grey caption. D-sap-3 — the prompt sent to the subagent and
// its final result are ALSO surfaced as founder-readable prose (the raw
// <pre> shape stays developer-only), so the founder can read what was
// asked and what came back without the developer toggle.
//
// Indent strategy for 375px viewports (spec §7): each level indents by
// pl-3 (12px) + border-l only — 5 levels cost 60px, leaving ~300px of
// usable width. No per-level padding multiplication.

import { useMemo } from 'react';
import { BotIcon } from 'lucide-react';
import { Icon } from './atoms/Icon';
import type { Message, SystemTaskNotificationContent } from '../types';
import {
  asAssistantText,
  asToolResult,
  asToolUse,
  pairToolResults,
  toolResultText,
  SUBAGENT_TOOL_NAMES,
  isBackgroundSubagentLaunch,
  ackIsRealEnd,
  type BackgroundLaunchCtx,
} from '../lib/messages';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
} from './ai-elements/chain-of-thought';
import { Loader } from './ai-elements/loader';
// DS.8 (D9 + D1/D3's byte-identity rule) — `ui/badge.tsx` retires here too, but
// NOT onto `@ds/data-display/Chip`: the DS Chip is a `<button>` with a fixed
// `height` per size and `min-width: 64px` [medido: Chip.module.css at the pinned
// v0.2.0], while the depth badge is an inline `<div>` sized by its own padding —
// and `subagent-panel-gallery` is an in-frame baseline that DS.8 may not move by
// a single byte (D13: the accepted-diff list is EMPTY). So this element takes
// D3's declared fallback: PRODUCT BLOCK, re-skinned on roles with today's markup,
// which retires the shadcn file just as completely. The classes below are the
// `badgeVariants` base + `secondary` variant verbatim, minus the four the call
// site already overrode (px/py/font-weight/text-size).
import { MessageBubble } from './MessageBubble';
import type { RowLivenessCtx } from '../lib/tool-rendering/founder-layer';
import { useTranslation } from '../i18n';
import { DeveloperOnly } from './founder/DeveloperOnly';
import { modelLabel } from '../lib/models';

// Hard ceiling on render recursion. The runtime supports 5 levels; the
// ceiling exists so malformed histories (a parent_tool_use_id cycle)
// degrade to flat ToolCallRow rendering instead of a stack overflow.
const MAX_RENDER_DEPTH = 8;

export interface SubagentPanelProps {
  /** Sprint orphan-tool-rows-honest (D013-EC-147) — per-message
   * plausibly-live ctx for child tool rows (parent-agent working gate,
   * spec D4). Threaded recursively into nested panels. */
  ctxForMessage?: (m: Message) => RowLivenessCtx;
  // The parent tool_use message (the Agent/Task call itself).
  toolUseMessage: Message;
  // Full parent_tool_use_id → children map from groupBySubagent(). The
  // panel looks up its own immediate children and hands the same map to
  // nested panels.
  childrenByParent: Map<string, Message[]>;
  // The matching tool_result (the subagent's final response), if landed.
  resultMessage?: Message;
  // 1-based nesting level. ConversationList always mounts at 1.
  depth?: number;
  // Sprint dispatch-lineage-ui (D4b) — three OPTIONAL, backward-compatible
  // seams so a dispatched worker (`DispatchWorkerCard`) renders through
  // THIS component instead of a clone of it: `label` replaces the
  // "Subagent" noun ("Trabajador"), `statusLabel` replaces the
  // running/error caption with the ledger state, `onOpenChange` lets the
  // adapter load the worker's transcript lazily on first expand. Every
  // existing call site omits all three and renders pixel-identically.
  label?: string;
  statusLabel?: string;
  onOpenChange?: (open: boolean) => void;
  /** Gate 2 fix round (reviewer, 2026-09-13, blocker 1) — a settled-without-error state is not
   * always "terminado": `DispatchWorkerCard`'s `needs_founder` terminal renders through the SAME
   * `usesAckAsEnd`-settled branch (a synthetic non-error `tool_result`) but the worker stopped to
   * ASK the founder, not finish. Defaults `true` (every existing call site — real subagents,
   * every OTHER dispatch terminal — keeps today's behaviour); `DispatchWorkerCard` passes
   * `dispatchCardStatus(...).done` explicitly. Gates ONLY the done predicate (`data-done` +
   * the Check glyph), never the `statusLabel` caption itself — "(needs your approval)" must
   * still render. */
  settledIsDone?: boolean;
  /** Sprint subagent-launch-ack-liveness (D3/D4) — contexto del lanzamiento en segundo plano
   * (`BackgroundLaunchCtx`, `lib/messages.ts`): fin real por `tool_use_id`, cota de edad y
   * gate de tarjeta pendiente. Calculado UNA vez en ConversationList sobre TODOS los
   * mensajes, pasado recursivo igual que `childrenByParent` y por la misma razón que éste no
   * puede derivarse por nivel: el fin se persiste en la raíz (EC-a). Ausente (call sites
   * legacy / DispatchWorkerCard / tests) ⇒ comportamiento pre-sprint idéntico. */
  bgCtx?: BackgroundLaunchCtx;
}

// Count every tool_use in the subtree below `parentId`, descending into
// nested subagents. Used for the `(+M)` descendant segment.
function countSubtreeTools(
  parentId: string,
  childrenByParent: Map<string, Message[]>,
  depthGuard = 0,
): number {
  if (depthGuard > MAX_RENDER_DEPTH) return 0;
  let n = 0;
  for (const m of childrenByParent.get(parentId) ?? []) {
    if (m.type !== 'tool_use') continue;
    n += 1;
    const tu = asToolUse(m);
    if (SUBAGENT_TOOL_NAMES.has(tu.name) && tu.tool_use_id) {
      n += countSubtreeTools(tu.tool_use_id, childrenByParent, depthGuard + 1);
    }
  }
  return n;
}

export function SubagentPanel({
  toolUseMessage,
  childrenByParent,
  resultMessage,
  depth = 1,
  ctxForMessage,
  label,
  statusLabel,
  onOpenChange,
  bgCtx,
  settledIsDone = true,
}: SubagentPanelProps) {
  const { t } = useTranslation();

  const tu = asToolUse(toolUseMessage);
  const tr = resultMessage ? asToolResult(resultMessage) : null;

  const childMessages = useMemo(
    () =>
      (tu.tool_use_id ? childrenByParent.get(tu.tool_use_id) : undefined) ?? [],
    [childrenByParent, tu.tool_use_id],
  );

  // Subagent prompt + type live in the input. Anthropic exposes
  // `subagent_type` (e.g. "explore", "shell", "generalPurpose") and
  // `prompt` (the instructions for the subagent).
  const subagentType =
    typeof tu.input.subagent_type === 'string'
      ? tu.input.subagent_type
      : typeof tu.input.description === 'string'
        ? tu.input.description
        : 'subagent';
  const subagentPrompt =
    typeof tu.input.prompt === 'string' ? tu.input.prompt : '';

  // Count tool calls inside the subagent for the header summary.
  const toolCount = useMemo(
    () => childMessages.filter((m) => m.type === 'tool_use').length,
    [childMessages],
  );

  // Tools run by NESTED subagents (subtree minus immediate). Zero for a
  // depth-1-only history, so the `(+M)` segment never renders there.
  const descendantTools = useMemo(
    () =>
      tu.tool_use_id
        ? countSubtreeTools(tu.tool_use_id, childrenByParent) - toolCount
        : 0,
    [tu.tool_use_id, childrenByParent, toolCount],
  );

  const hasNestedSubagent = useMemo(
    () =>
      childMessages.some(
        (m) =>
          m.type === 'tool_use' && SUBAGENT_TOOL_NAMES.has(asToolUse(m).name),
      ),
    [childMessages],
  );

  // Depth badge only when the tree actually nests: any nested panel
  // (depth > 1) shows one, and the root shows N1 only when it has
  // subagent children. Keeps flat histories pixel-identical (spec §6.7).
  const showDepthBadge = depth > 1 || hasNestedSubagent;

  // Model this subagent is running as. Canonical source is the model
  // stamped on the subagent's own assistant_text children (persisted by
  // session-manager on every assistant event). Task input.model is only
  // set when the parent explicitly overrode the model, so it's a
  // fallback, not a primary source. If neither is present, we render no
  // model segment — never guess (santi-context: "Never fabricate").
  const model = useMemo<string | null>(() => {
    const firstText = childMessages.find((m) => m.type === 'assistant_text');
    if (firstText) {
      const parsed = asAssistantText(firstText);
      if (typeof parsed.model === 'string' && parsed.model.length > 0) {
        return parsed.model;
      }
    }
    if (typeof tu.input.model === 'string' && tu.input.model.length > 0) {
      return tu.input.model;
    }
    return null;
  }, [childMessages, tu.input.model]);

  // ── D4 (D013-EC-659) ────────────────────────────────────────────────────────
  // AIER-3 refinado, no revertido: cada nivel sigue derivando su estado de SU PROPIA
  // evidencia. Lo que cambia es CUÁL es su evidencia — para un lanzamiento en segundo plano
  // el `tool_result` es el RECIBO del lanzamiento (medido 89-115 ms, n=3/3, transcript
  // 941230e9, 2026-09-12), no el resultado.
  const isBgLaunch = isBackgroundSubagentLaunch(toolUseMessage);
  const ackIsError = ackIsRealEnd(resultMessage); // EC-e
  const usesAckAsEnd = !isBgLaunch || ackIsError; // el camino de HOY, intacto (AC6)

  const completionMessage =
    isBgLaunch && !ackIsError && tu.tool_use_id
      ? bgCtx?.completionByToolUseId.get(tu.tool_use_id)
      : undefined;
  const notif = completionMessage
    ? (completionMessage.content as SystemTaskNotificationContent | null)
    : null;

  type BgState = 'running' | 'settled' | 'unconfirmed';
  // ORDEN DE PRECEDENCIA, y es intencional:
  //  1. el fin persistido gana siempre (cubre FM4: un `agent.working` que llega tarde por un
  //     WS reordenado no puede volver a "no sé" algo que YA sabemos);
  //  2. la cota de edad (B3): un lanzamiento que sobrevivió a dos cierres de turno duros no
  //     está "trabajando" por mucho que el padre trabaje en otra cosa;
  //  3. el padre parado — PERO no si está bloqueado en una tarjeta (C13): ahí `working:false`
  //     es un artefacto de `rollup.ts:522`+`:533`, no una parada.
  const bgState: BgState = notif
    ? 'settled'
    : bgCtx !== undefined && bgCtx.staleLaunchIds.has(toolUseMessage.id)
      ? 'unconfirmed'
      : bgCtx !== undefined && bgCtx.agentWorking === false && !bgCtx.parentBlockedOnCard
        ? 'unconfirmed'
        : 'running';

  // C8 — UN solo vocabulario. Error ⇔ hay un `status` y NO es `completed`. El complemento, no
  // una lista: un estado terminal futuro que la CLI añada cae del lado honesto/ruidoso en vez
  // de pintarse en calma por no estar en un set que nadie actualizó.
  const isError = usesAckAsEnd
    ? tr?.is_error === true
    : bgState === 'settled' && notif?.status != null && notif.status !== 'completed';
  // Edge case (spec §7): a nested subagent can still be running while an
  // ancestor already resolved — each level derives running purely from
  // its OWN missing tool_result, never from the ancestor's state.
  const isRunning = usesAckAsEnd ? !tr : bgState === 'running';
  const isUnconfirmed = !usesAckAsEnd && bgState === 'unconfirmed';
  // Gate 2 fix round (blocker 1) — settled-without-error is not always "terminado" (see
  // `settledIsDone` prop doc). Gates ONLY `data-done` + the Check glyph below, never the
  // `statusLabel` caption block, which must still render "(needs your approval)" etc.
  const isDone = !isRunning && !isError && !isUnconfirmed && settledIsDone;

  // El recibo NUNCA es el cuerpo founder: el propio recibo pide no citarse ("This tool result
  // is internal metadata — never quote…").
  const resultBody: string | null = usesAckAsEnd
    ? (tr ? toolResultText(tr.content) : null)
    : bgState === 'settled'
      ? (notif?.summary ?? '')
      : null; // running / unconfirmed: no hay resultado, no se inventa

  // Derive duration from the timestamps of the tool_use vs tool_result.
  const duration = useMemo(() => {
    if (usesAckAsEnd) {
      // Camino pre-sprint, byte a byte.
      if (!resultMessage) return null;
      const start = Date.parse(toolUseMessage.timestamp);
      const end = Date.parse(resultMessage.timestamp);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
      return Math.max(0, end - start);
    }
    if (!completionMessage || !notif) return null;
    // `usage.duration_ms` cuando el evento estructurado la trajo (F8); si no, la resta de
    // timestamps persistidos. NUNCA la del recibo.
    if (typeof notif.duration_ms === 'number' && Number.isFinite(notif.duration_ms) && notif.duration_ms >= 0) {
      return notif.duration_ms;
    }
    const start = Date.parse(toolUseMessage.timestamp);
    const end = Date.parse(completionMessage.timestamp);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    return Math.max(0, end - start);
  }, [usesAckAsEnd, resultMessage, completionMessage, notif, toolUseMessage]);

  // Each recursion level pairs its OWN immediate children — a nested
  // Task's tool_result is emitted inside the parent subagent's stream,
  // so it lives in this level's child list.
  const { resultByToolUseId, pairedResultIds } = useMemo(
    () => pairToolResults(childMessages),
    [childMessages],
  );

  return (
    <div
      className={`border rounded-lg overflow-hidden text-body ${
        isError
          ? 'border-status-error/40 bg-status-error/10'
          : 'border-border bg-popover/60'
      }`}
      data-testid="subagent-panel"
      data-subagent-type={subagentType}
      data-error={isError ? 'true' : 'false'}
      data-running={isRunning ? 'true' : 'false'}
      data-unconfirmed={isUnconfirmed ? 'true' : 'false'}
      data-done={isDone ? 'true' : 'false'}
      data-depth={depth}
    >
      {/* Sprint subagent-panel-ux (D-sap-1) — `max-w-none` overrides the
          primitive's `max-w-prose` (585px) cap at THIS call site only
          (twMerge: last wins). The subagent panel fills its border width
          at every depth; the primitive keeps its reading-width cap for
          Reasoning and other prose consumers. Depth is signalled by the
          `pl-3 border-l` indent, not by width. */}
      <ChainOfThought defaultOpen={false} className="max-w-none" onOpenChange={onOpenChange}>
        <ChainOfThoughtHeader
          icon={BotIcon}
          data-testid="subagent-header"
          className={`px-3 py-2 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-border ${
            isError ? 'hover:bg-status-error/15' : ''
          }`}
        >
          <span
            className={`flex min-w-0 items-center gap-2 text-body ${
              isError ? 'text-status-error' : 'text-foreground'
            }`}
          >
            {showDepthBadge && (
              <div
                className="inline-flex items-center rounded-md border border-transparent bg-secondary text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0 px-2 py-0.5 font-normal text-caption"
                data-testid="subagent-depth-badge"
              >
                {t('subagent.depthBadge', { depth })}
              </div>
            )}
            <span className="truncate">
              {label ?? t('subagent.label')} · {subagentType}
              {model !== null && ` · ${modelLabel(model)}`}
              {/* Sprint messages-section-fixes (D-msg-16) — only show the
                  tool count when there is at least one tool call. Pre-fix,
                  subagents that responded with text only rendered "0 tools",
                  which was confusing — a "tools" segment that says zero is
                  worse than no segment at all. */}
              {toolCount > 0 && (
                <>
                  {' · '}
                  {toolCount === 1
                    ? t('subagent.tools', { count: toolCount })
                    : t('subagent.toolsPlural', { count: toolCount })}
                  {descendantTools > 0 &&
                    ` ${t('subagent.descendantTools', { count: descendantTools })}`}
                </>
              )}
              {duration !== null && ` · ${(duration / 1000).toFixed(1)}s`}
              {/* Founder directive 2026-09-13 (mockup approval, verbatim: "si el trabajo está
                  completado, ponga un check"): finished-without-error, the same green Check
                  glyph the tool rows already render (ToolCallRow.tsx `default` branch — same
                  Icon component, size, colour token). Covers BOTH paths that land here — a
                  foreground launch's real `tool_result` and a background launch's `settled`
                  state — "terminado" is one state, not two. Renders before any `statusLabel`
                  caption (DispatchWorkerCard's "(reported)" keeps its own span, untouched).
                  Gate 2 fix round (blocker 1) — gated on `isDone`, not the bare settled
                  predicate: a `DispatchWorkerCard` `needs_founder` terminal is settled and
                  non-error but is NOT done (`settledIsDone={false}`). */}
              {isDone && (
                <Icon
                  name="Check"
                  size={14}
                  // `inline` overrides Tailwind preflight's `svg { display: block }` — without
                  // it the glyph drops to its own line inside this text-flow `truncate` span
                  // (verified visually while regenerating the mockup capture, R2). The other
                  // header segments avoid this because they wrap their icon in an
                  // `inline-flex` span (see the Loader spinner below); a bare icon needs the
                  // class directly.
                  className="ml-2 inline shrink-0 align-text-bottom text-status-success"
                  aria-label={t('subagent.done')}
                  data-testid="subagent-done-check"
                />
              )}
              {/* Sprint subagent-panel-ux (D-sap-2) — the running signal
                  was static grey caption text (invisible at a glance).
                  Now an animated spoke Loader + label, inheriting the
                  header's foreground colour — coherent with StatusIcon's
                  `running` spinner (the same "working" motion Santi reads
                  in the sidebar). Rendered in the header, so it's visible
                  while the panel is collapsed. Motion is the glanceable
                  cue (cursor-ux-mirror §2.3/§2.5); accent colour is NOT
                  used because `accent` is a neutral hover surface, not an
                  attention token, and the app's running convention is a
                  currentColor spinner. */}
              {isRunning && (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-caption"
                  role="status"
                  aria-label={statusLabel ?? t('subagent.running')}
                >
                  <Loader size={12} />
                  {statusLabel ?? t('subagent.running')}
                </span>
              )}
              {!isRunning && isError && (
                <span className="ml-2 text-caption text-status-error" data-testid="subagent-status">
                  {statusLabel ?? (notif?.status ? `${t('subagent.error')} ${notif.status}` : t('subagent.error'))}
                </span>
              )}
              {/* Sprint subagent-launch-ack-liveness (D4, C9) — sin fin confirmado, y no puede
                  llamarse "trabajando" (padre parado o cota de edad, D3.staleLaunchIds). Nunca
                  "terminado" — el cuarto estado de D-028 aplicado a la tarjeta. */}
              {!isRunning && !isError && isUnconfirmed && (
                <span className="ml-2 text-caption text-muted-foreground" data-testid="subagent-status">
                  {statusLabel ?? t('subagent.unconfirmed')}
                </span>
              )}
              {/* Sprint dispatch-lineage-ui (D4b) — a settled, non-error
                  state with its own caption (e.g. "(reportó)"). Existing
                  callers never pass one, so nothing renders here for them. */}
              {!isRunning && !isError && !isUnconfirmed && statusLabel && (
                <span className="ml-2 text-caption text-muted-foreground" data-testid="subagent-status">
                  {statusLabel}
                </span>
              )}
            </span>
          </span>
        </ChainOfThoughtHeader>
        <ChainOfThoughtContent
          className="mt-0 border-t border-border px-3 py-2 space-y-2"
          data-testid="subagent-body"
        >
          {/* Sprint subagent-panel-ux (D-sap-3) — founder-readable prompt
              block (always rendered = founder layer, founder-first §3).
              The subagent prompt is natural-language instructions, not
              code — surfacing it as prose is "what the agent did in plain
              language" the founder needs (Santi chat 2026-07-22: "ver el
              mensaje que le ha enviado al subagente"). Only renders inside
              the expanded body (defaultOpen=false), so it never adds noise
              to the collapsed view. Chesterton's fence (D-msg-12): that
              decision hid the developer-SHAPED raw <pre>; D-sap-3 keeps the
              raw shape developer-only (below) and adds this humanised layer
              — consistent with the fence's intent, not a reversal. */}
          {subagentPrompt && (
            <div data-testid="subagent-prompt-founder">
              <div className="text-caption uppercase tracking-wide text-muted-foreground/70 mb-1">
                {t('subagent.promptFounder')}
              </div>
              <div className="text-body whitespace-pre-wrap break-words text-foreground">
                {subagentPrompt}
              </div>
            </div>
          )}

          {/* D-msg-12 — the raw prompt `<pre>` (monospace, verbatim) stays
              developer-only; the founder prose above is the founder layer. */}
          {subagentPrompt && (
            <DeveloperOnly>
              <div>
                <div className="text-caption uppercase tracking-wide text-muted-foreground/70 mb-1">
                  {t('subagent.prompt')}
                </div>
                <pre className="bg-background border border-border rounded text-xs p-2 overflow-x-auto whitespace-pre-wrap break-words text-foreground"> {/* design-discipline: kept as text-xs — developer-mode subagent prompt */}
                  {subagentPrompt}
                </pre>
              </div>
            </DeveloperOnly>
          )}

          {childMessages.length > 0 && (
            <div className="space-y-2" data-testid="subagent-child-list">
              {childMessages.map((m) => {
                if (m.type === 'tool_result' && pairedResultIds.has(m.id)) {
                  return null;
                }
                let pairedResult: Message | undefined;
                let childTuId = '';
                let isNestedSubagent = false;
                if (m.type === 'tool_use') {
                  const childTu = asToolUse(m);
                  childTuId = childTu.tool_use_id;
                  if (childTuId) {
                    pairedResult = resultByToolUseId.get(childTuId);
                  }
                  isNestedSubagent =
                    SUBAGENT_TOOL_NAMES.has(childTu.name) && childTuId !== '';
                }
                return (
                  <div
                    key={m.id}
                    className="pl-3 border-l border-border"
                    data-message-id={m.id}
                  >
                    {isNestedSubagent && depth < MAX_RENDER_DEPTH ? (
                      <SubagentPanel
                        toolUseMessage={m}
                        childrenByParent={childrenByParent}
                        resultMessage={pairedResult}
                        depth={depth + 1}
                        ctxForMessage={ctxForMessage}
                        bgCtx={bgCtx}
                      />
                    ) : (
                      <MessageBubble
                        message={m}
                        toolResultMessage={pairedResult}
                        toolLivenessCtx={ctxForMessage?.(m)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Sprint subagent-panel-ux (D-sap-3) — founder-readable result
              block (always rendered = founder layer). Santi chat
              2026-07-22: "ver ... lo que renderiza el subagente". The
              subagent's final answer is prose; surfacing it in founder
              mode closes the loop the panel opens. Error label + colour
              preserved from the header signal. */}
          {resultBody !== null && (
            <div data-testid="subagent-result-founder">
              <div
                className={`text-caption uppercase tracking-wide mb-1 ${
                  isError ? 'text-status-error' : 'text-muted-foreground/70'
                }`}
              >
                {isError ? t('subagent.subagentError') : t('subagent.resultFounder')}
              </div>
              <div
                className={`text-body whitespace-pre-wrap break-words ${
                  isError ? 'text-status-error' : 'text-foreground'
                }`}
              >
                {resultBody || (
                  <span className="text-muted-foreground/70">{t('subagent.emptyResult')}</span>
                )}
              </div>
            </div>
          )}

          {/* D-msg-12 — the raw final-result `<pre>` (monospace, verbatim)
              stays developer-only; the founder prose above is the founder
              layer. The error case is also surfaced in the header chip. */}
          {resultBody !== null && (
            <DeveloperOnly>
              <div>
                <div
                  className={`text-caption uppercase tracking-wide mb-1 ${
                    isError ? 'text-status-error' : 'text-muted-foreground/70'
                  }`}
                >
                  {isError ? t('subagent.subagentError') : t('subagent.finalResult')}
                </div>
                <pre
                  className={`rounded text-xs p-2 overflow-x-auto whitespace-pre-wrap break-words bg-background border ${ /* design-discipline: kept as text-xs — developer-mode subagent result */
                    isError
                      ? 'border-status-error/40 text-status-error'
                      : 'border-border text-foreground'
                  }`}
                >
                  {resultBody || (
                    <span className="text-muted-foreground/70">{t('subagent.emptyResult')}</span>
                  )}
                </pre>
              </div>
            </DeveloperOnly>
          )}

          {/* Sprint subagent-launch-ack-liveness (D4, item 8) — el recibo de lanzamiento tal
              cual, SIEMPRE en capa developer cuando el camino es "en segundo plano" (incluso
              con `resultBody === null`: en `running`/`unconfirmed` el recibo es lo ÚNICO que
              hay, y ocultarlo escondería el dato honesto que D4 pide mostrar). Primer plano
              (`usesAckAsEnd`) no entra aquí — ese caso ya tiene su `<pre>` de siempre arriba. */}
          {!usesAckAsEnd && tr && (
            <DeveloperOnly>
              <div data-testid="subagent-launch-ack">
                <div className="text-caption uppercase tracking-wide text-muted-foreground/70 mb-1">
                  {t('subagent.launchAck')}
                </div>
                <pre className="bg-background border border-border rounded text-xs p-2 overflow-x-auto whitespace-pre-wrap break-words text-foreground"> {/* design-discipline: kept as text-xs — developer-mode launch receipt */}
                  {toolResultText(tr.content)}
                </pre>
              </div>
            </DeveloperOnly>
          )}
        </ChainOfThoughtContent>
      </ChainOfThought>
    </div>
  );
}
